import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { countryLabel, flagFromCode } from "@/lib/countries";
import { Search, Languages, Mail, Linkedin, ArrowLeft, MapPin, Briefcase, X } from "lucide-react";

export default function Partners() {
  const { t, lang } = useI18n();
  const [countries, setCountries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const loadCountries = async () => {
    const { data } = await api.get("/public/partners/countries");
    setCountries(data);
  };
  const loadPartners = async (country) => {
    const url = country ? `/public/partners?country=${encodeURIComponent(country)}` : "/public/partners";
    const { data } = await api.get(url);
    setPartners(data);
  };

  useEffect(() => { loadCountries(); loadPartners(null); }, []);

  const filtered = partners.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      p.name?.toLowerCase().includes(s) ||
      p.bio?.toLowerCase().includes(s) ||
      countryLabel(p.country, p.country_code, lang).toLowerCase().includes(s) ||
      (p.services || []).some((x) => x.toLowerCase().includes(s)) ||
      (p.industries || []).some((x) => x.toLowerCase().includes(s))
    );
  });

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="partners-brand">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" /> {lang === "el" ? "Αρχική" : "Home"}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0052CC]" />
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, rgba(0,82,204,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.3) 0%, transparent 50%)"
          }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs uppercase tracking-[0.2em] font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            {lang === "el" ? "Παγκόσμιο δίκτυο" : "Global network"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            {lang === "el" ? "Ανακαλύψτε συνεργάτες πωλήσεων σε κάθε χώρα." : "Discover sales partners in every country."}
          </h1>
          <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
            {lang === "el"
              ? "Επιλέξτε χώρα και βρείτε έμπειρους επαγγελματίες πωλήσεων που γνωρίζουν την τοπική αγορά, μιλούν τη γλώσσα και προωθούν τα ελληνικά σας προϊόντα."
              : "Pick a country and meet experienced sales professionals who know the local market, speak the language, and promote your Greek products."}
          </p>
        </div>
      </section>

      {/* Country chips */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10">
        <div className="flex flex-wrap items-center gap-2" data-testid="country-chips">
          <button
            onClick={() => { setActive(null); loadPartners(null); }}
            data-testid="country-chip-all"
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
              active === null ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0052CC]/40"
            }`}
          >
            🌍 {lang === "el" ? "Όλες οι χώρες" : "All countries"}
            <span className="ml-2 text-xs opacity-70">{countries.reduce((s, c) => s + c.count, 0)}</span>
          </button>
          {countries.map((c) => (
            <button
              key={c.country}
              data-testid={`country-chip-${c.country}`}
              onClick={() => { setActive(c.country); loadPartners(c.country); }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                active === c.country ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0052CC]/40"
              }`}
            >
              <span className="mr-1.5">{flagFromCode(c.country_code)}</span>
              {countryLabel(c.country, c.country_code, lang)}
              <span className="ml-2 text-xs opacity-70">{c.count}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="partners-search-input"
            placeholder={lang === "el" ? "Αναζήτηση συνεργατών, κλάδων, υπηρεσιών..." : "Search partners, industries, services..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40"
          />
        </div>
      </section>

      {/* Partners grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        {filtered.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg py-16 px-6 text-center" data-testid="partners-empty">
            <div className="text-6xl mb-4">🌍</div>
            <h3 className="font-heading font-semibold text-slate-900 text-xl">
              {lang === "el" ? "Δεν υπάρχουν συνεργάτες ακόμη" : "No partners yet"}
            </h3>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
              {lang === "el"
                ? "Οι Super Admins μπορούν να προσθέσουν συνεργάτες από τη διαχείριση. Επιστρέψτε σύντομα!"
                : "Super Admins can add partners from the admin panel. Check back soon!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="partners-grid">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                data-testid={`partner-card-${p.id}`}
                className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden hover:-translate-y-1.5 hover:shadow-2xl hover:border-[#0052CC]/40 transition-all duration-300"
              >
                {/* Big photo */}
                <div className="relative aspect-[4/5] bg-gradient-to-br from-slate-200 via-slate-100 to-slate-50 overflow-hidden">
                  {p.photo_url ? (
                    <img
                      src={`${BACKEND}${p.photo_url}`}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-heading text-7xl font-bold text-slate-300">{p.name?.[0]}</span>
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/15 to-transparent" />

                  {/* Country flag chip - top right */}
                  <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-slate-900 shadow-lg">
                    <span className="text-base leading-none">{flagFromCode(p.country_code)}</span>
                    {countryLabel(p.country, p.country_code, lang)}
                  </div>

                  {/* Years experience badge - top left */}
                  {p.years_experience != null && p.years_experience > 0 && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-xs font-bold shadow-lg">
                      {p.years_experience}+ {lang === "el" ? "χρόνια" : "yrs"}
                    </div>
                  )}

                  {/* Name & title overlay - bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="font-heading font-bold text-2xl leading-tight tracking-tight">{p.name}</h3>
                    {p.title && <div className="text-sm text-slate-200 mt-1 font-medium">{p.title}</div>}
                    {(p.city || p.country) && (
                      <div className="mt-2 text-xs text-slate-300 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {p.city ? `${p.city}, ` : ""}{countryLabel(p.country, p.country_code, lang)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card body */}
                <div className="p-6 space-y-4">
                  {p.bio && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{p.bio}</p>
                  )}

                  {p.services?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2 flex items-center gap-1.5">
                        <Briefcase className="w-3 h-3" />
                        {lang === "el" ? "Υπηρεσίες" : "Services"}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.services.slice(0, 4).map((s) => (
                          <span key={s} className="text-[11px] px-2.5 py-1 bg-[#0052CC]/10 text-[#0052CC] rounded-full font-semibold">{s}</span>
                        ))}
                        {p.services.length > 4 && <span className="text-[11px] px-2.5 py-1 text-slate-500 font-semibold">+{p.services.length - 4}</span>}
                      </div>
                    </div>
                  )}

                  {p.languages?.length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 pt-2 border-t border-slate-100">
                      <Languages className="w-3.5 h-3.5" />
                      <span className="font-medium">{p.languages.join(" · ")}</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden my-8" onClick={(e) => e.stopPropagation()} data-testid="partner-detail-modal">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Photo side */}
              <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[600px] bg-gradient-to-br from-slate-200 to-slate-100 overflow-hidden">
                {selected.photo_url ? (
                  <img src={`${BACKEND}${selected.photo_url}`} alt={selected.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-heading text-9xl font-bold text-slate-300">{selected.name?.[0]}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
                <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-slate-900 shadow-lg">
                  <span className="text-base leading-none">{flagFromCode(selected.country_code)}</span>
                  {countryLabel(selected.country, selected.country_code, lang)}
                </div>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="partner-detail-close"
                  className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center text-slate-700 hover:bg-white shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                  <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-tight">{selected.name}</h2>
                  {selected.title && <div className="text-base text-slate-200 mt-1 font-medium">{selected.title}</div>}
                  <div className="text-xs text-slate-300 mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selected.city ? `${selected.city}, ` : ""}{countryLabel(selected.country, selected.country_code, lang)}
                  </div>
                </div>
              </div>

              {/* Info side */}
              <div className="p-6 md:p-8 max-h-[600px] overflow-y-auto">
                {selected.bio && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2">
                      {lang === "el" ? "Βιογραφικό" : "About"}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.bio}</p>
                  </div>
                )}

                {selected.services?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2">
                      {lang === "el" ? "Υπηρεσίες" : "Services"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.services.map((s) => (
                        <span key={s} className="px-3 py-1 bg-[#0052CC]/10 text-[#0052CC] rounded-full text-xs font-semibold">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {selected.industries?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2">
                      {lang === "el" ? "Κλάδοι" : "Industries"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.industries.map((s) => (
                        <span key={s} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  {selected.languages?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-1">
                        {lang === "el" ? "Γλώσσες" : "Languages"}
                      </div>
                      <div className="text-slate-700 font-medium">{selected.languages.join(", ")}</div>
                    </div>
                  )}
                  {selected.years_experience != null && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-1">
                        {lang === "el" ? "Εμπειρία" : "Experience"}
                      </div>
                      <div className="text-slate-700 font-medium">
                        {selected.years_experience} {lang === "el" ? "χρόνια" : "years"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} data-testid="partner-detail-email" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold hover:bg-[#0043A8]">
                      <Mail className="w-4 h-4" /> {lang === "el" ? "Επικοινωνία" : "Contact"}
                    </a>
                  )}
                  {selected.linkedin && (
                    <a href={selected.linkedin} target="_blank" rel="noreferrer" data-testid="partner-detail-linkedin" className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-700 rounded-md text-sm font-semibold hover:bg-slate-50">
                      <Linkedin className="w-4 h-4" /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
